import type {Language, Skill, UserBookmarks} from "~/types/user";
import {UserLastVisited} from "~/types/constants";
import dayjs from "dayjs";

export const useUserStore = defineStore('user', () => {
    const localStorageKey = 'wad.state'
    const { $wadAcl } = useNuxtApp()
    const { user } = useAuthUser();
    const { getDeveloperSeniority } = useMatching();
    const { updateUserLanguages, updateUserSkills } = useSkillSlider();
    const skills = ref<Skill[]>([...(user.value?.skills || [])])
    const languages = ref<Language[]>([...(user.value?.languages || [])])
    const after_developer_signup_url = ref<string|null>(null)
    const email_verified = ref<boolean|null>(user.value?.email_verified)
    const show_after_signup_wizard = ref<boolean|null>(user.value?.show_after_signup_wizard)
    const non_developer_skills = ref<Skill[]|null>(null)
    const non_developer_languages = ref<Language[]|null>(null)
    const seniority = ref<number>(user.value?.seniority);
    const bookmarks = ref<UserBookmarks>({})

    watch(
      () => user.value?.bookmarks,
      (v) => {
        bookmarks.value = v
          ? JSON.parse(JSON.stringify(v))
          : {}
      },
      { immediate: true }
    )

    //region getters

    function userSkillValue(skillId?: number) {
      if(!skillId) {
        return 0;
      }
      skillId = Number(skillId);
      let skill = null;
      if(user.value.skills && user.value.skills.length > 0) {
        skill = user.value.skills.find((e) => e.id === skillId);
      }
      if(skill) {
        return Number(skill.years_of_experience);
      }
      return 0;
    }

    function userLanguageLevel(languageId?: number) {
      languageId = Number(languageId);
      const languages = $wadAcl.isDeveloper() ? user.value.languages : non_developer_languages?.value;
      const language = languages?.find((language) => language.id === languageId);

      return language ? Number(language.level_id) : 0;
    }

    const isBookmarked = (type: string, id: number) => {
      return bookmarks.value[type]?.includes(id) ?? false
    }

    //endregion

    //region setters

    const setUserSkills = (skill: Skill) => {
      const level = Number(skill.years_of_experience);
      const idx = skills.value.findIndex(s => s.id === skill.id)
      if(idx === -1) {
        if(level > 0) {
          skills.value.push(skill);
        }
      } else {
        if(level > 0) {
          if(skills.value.at(idx)) {
            skills.value.at(idx)!.years_of_experience = level
          }
        } else {
          skills.value.splice(idx, 1);
        }
      }

      seniority.value = getDeveloperSeniority(skills.value.map(s => s.years_of_experience))
      if($wadAcl.isDeveloper()) {
        updateUserSkills(skills.value)
      } else {
        non_developer_skills.value = [...skills.value]
      }
      persistPartial()
    }

    function setUserLanguage(language?: Language) {
      const langIdx = languages.value.findIndex(l => l.id === language?.id)
      if(langIdx >= 0) {
        languages.value.at(langIdx)!.level_id = Number(language?.level_id)
      } else {
        languages.value.push(language as Language);
      }

      languages.value.sort((a, b) => {
        return Number(b.level_id) - Number(a.level_id)
      })

      if($wadAcl.isDeveloper()) {
        updateUserLanguages(languages.value)
      } else {
        non_developer_languages.value = [...languages.value]
      }
      persistPartial()
    }

    function toggleBookmark(type: string, id: number, next: boolean) {
      const current = bookmarks.value[type] ?? []
      bookmarks.value = {
        ...bookmarks.value,
        [type]: next
          ? Array.from(new Set([...current, id]))
          : current.filter(x => x !== id),
      }
      persistPartial()
    }

    const setAfterDeveloperSignupUrl = (url: string|null) => {
      after_developer_signup_url.value = url;
      persistPartial()
    }

    const clearNonDevSkills = () => {
      non_developer_skills.value = null;
      localStorage.removeItem(`${localStorageKey}.user.non_developer.skills`)
    }

    const clearNonDevLanguages = () => {
      non_developer_languages.value = null;
      localStorage.removeItem(`${localStorageKey}.user.non_developer.languages`)
    }

    //endregion

    //region methods

    const removeMenuNotification = (context: string) => {
      switch(true) {
        case context === UserLastVisited.DeveloperDashboard:
          if(!user.value?.developer_dashboard_last_visited_at) {
            user.value.developer_dashboard_last_visited_at = dayjs().toISOString();
          }
          break;
        case context === UserLastVisited.CompanyDashboard:
          if(!user.value?.company_dashboard_last_visited_at) {
            user.value.company_dashboard_last_visited_at = dayjs().toISOString();
          }
          break;
        case context === UserLastVisited.CompanyUsers:
          if(!user.value?.company_users_last_visited_at) {
            user.value.company_users_last_visited_at = dayjs().toISOString();
          }
          break;
      }
    }

    //endregion

    //region local storage

    function persistPartial() {
      if(import.meta.client) {
        const stored = JSON.parse(localStorage.getItem(localStorageKey) || '{}')

        const updated = {
          ...stored,
          user: {
            ...(stored.user || {}),
            non_developer: {
              ...(stored.user?.non_developer || {}),
              skills: non_developer_skills.value,
              languages: non_developer_languages.value,
            },
          },
          navigation: {
            ...(stored.navigation || {}),
            after_developer_signup_url: after_developer_signup_url.value,
          }
        }

        localStorage.setItem(localStorageKey, JSON.stringify(updated))
      }
    }

    //endregion


    return {
      skills,
      non_developer_skills,
      seniority,
      languages,
      bookmarks,
      show_after_signup_wizard,
      email_verified,
      setUserSkills,
      userSkillValue,
      userLanguageLevel,
      setUserLanguage,
      isBookmarked,
      toggleBookmark,
      removeMenuNotification,
      setAfterDeveloperSignupUrl,
      after_developer_signup_url,
      clearNonDevSkills,
      clearNonDevLanguages,
    }
  });
